import { supabase, resolveFunctionError } from '@/lib/supabase';
import { createTextBlobUrl } from '@/lib/download';

// Áreas de reinicio disponibles. Cada `id` debe coincidir con las claves
// definidas en la Edge Function `reset-data`.
export interface ResetScope {
  id: string;
  label: string;
  icon: string;
  description: string;
  affects: string;
}

export const RESET_SCOPES: ResetScope[] = [
  {
    id: 'sales',
    label: 'Ventas y pedidos',
    icon: 'ri-shopping-bag-3-line',
    description:
      'Borra el ciclo comercial completo: pedidos, ventas, ítems, pagos de clientes, comisiones y todo el post-venta asociado.',
    affects: 'Pedidos · Ventas · Pagos · Comisiones · Devoluciones · Cambios · Garantías',
  },
  {
    id: 'postventa',
    label: 'Devoluciones, cambios y garantías',
    icon: 'ri-arrow-go-back-line',
    description:
      'Borra solo el post-venta, dejando intactas las ventas y los pedidos.',
    affects: 'Devoluciones · Cambios · Garantías · pagos de esas operaciones',
  },
  {
    id: 'inventory',
    label: 'Inventario (stock a cero)',
    icon: 'ri-stack-line',
    description:
      'Deja todo el stock disponible, reservado y en consignación en cero, y limpia el historial de movimientos. El catálogo se conserva.',
    affects: 'Stock de inventario · Movimientos · Barridos de reservas',
  },
  {
    id: 'purchases',
    label: 'Compras y cuentas por pagar',
    icon: 'ri-shopping-cart-2-line',
    description: 'Borra las compras registradas, sus ítems y los abonos a proveedores.',
    affects: 'Compras · Ítems de compra · Abonos a proveedores',
  },
  {
    id: 'consignments',
    label: 'Consignaciones',
    icon: 'ri-git-repository-line',
    description: 'Borra las consignaciones, su mercancía y los pagos recibidos de distribuidores.',
    affects: 'Consignaciones · Ítems · Pagos de consignación',
  },
  {
    id: 'finances',
    label: 'Gastos y donaciones',
    icon: 'ri-bill-line',
    description: 'Borra los gastos operativos y las donaciones sociales registradas.',
    affects: 'Gastos · Donaciones · Entregas de donación',
  },
  {
    id: 'customers',
    label: 'Clientes',
    icon: 'ri-user-heart-line',
    description:
      'Borra los clientes registrados. Requiere haber reiniciado antes ventas y pedidos, porque dependen de ellos.',
    affects: 'Clientes',
  },
  {
    id: 'marketing',
    label: 'Marketing (métricas y eventos)',
    icon: 'ri-megaphone-line',
    description:
      'Borra el registro diario de campañas y los eventos de Meta. Las campañas (configuración) se conservan.',
    affects: 'Métricas de campaña · Eventos de Meta',
  },
  {
    id: 'audit',
    label: 'Auditoría',
    icon: 'ri-history-line',
    description: 'Borra el historial de actividad del sistema.',
    affects: 'Registros de auditoría',
  },
];

export interface ResetResult {
  success: boolean;
  error?: string;
  results?: Array<{ table: string; deleted?: number; updated?: number }>;
}

// Resumen previo: cuántos registros hay en cada área (sin borrar nada) y
// cuántos pedidos abandonados todavía retienen stock reservado.
export interface ResetPreview {
  counts: Record<string, number>;
  abandoned: { orders: number; units: number };
}

export async function previewResetCounts(): Promise<ResetPreview> {
  const empty: ResetPreview = { counts: {}, abandoned: { orders: 0, units: 0 } };
  if (!supabase) return empty;
  try {
    const { data, error } = await supabase.functions.invoke('reset-data', {
      body: { action: 'preview' },
    });
    if (error) return empty;
    const payload = data as
      | { ok?: boolean; counts?: Record<string, number>; abandoned?: { orders?: number; units?: number } }
      | null;
    if (!payload?.ok) return empty;
    return {
      counts: payload.counts ?? {},
      abandoned: {
        orders: Number(payload.abandoned?.orders ?? 0),
        units: Number(payload.abandoned?.units ?? 0),
      },
    };
  } catch {
    return empty;
  }
}

export interface RunResetOptions {
  // Solo aplica al área "sales": devuelve al inventario el stock reservado por
  // pedidos abandonados antes de borrarlos.
  releaseReservations?: boolean;
}

// Ejecuta el reinicio de un área. La lógica real corre en el servidor.
export async function runReset(scope: string, options?: RunResetOptions): Promise<ResetResult> {
  if (!supabase) {
    return { success: false, error: 'Sin conexión a la base de datos.' };
  }
  const { data, error } = await supabase.functions.invoke('reset-data', {
    body: { scope, releaseReservations: options?.releaseReservations === true },
  });
  if (error) {
    const message = await resolveFunctionError(error);
    return { success: false, error: message };
  }
  const payload = data as { ok?: boolean; error?: string; results?: ResetResult['results'] } | null;
  if (!payload?.ok) {
    return { success: false, error: payload?.error ?? 'No se pudo completar el reinicio.' };
  }
  return { success: true, results: payload.results };
}

// Tablas que se incluyen en la copia de seguridad (datos de negocio).
// Se excluyen a propósito las tablas de configuración y credenciales.
const BACKUP_TABLES = [
  'products',
  'product_variants',
  'categories',
  'sizes',
  'inventory',
  'inventory_movements',
  'customers',
  'salespersons',
  'distributors',
  'suppliers',
  'orders',
  'order_items',
  'sales',
  'sale_items',
  'payments',
  'expenses',
  'commissions',
  'donations',
  'donation_entries',
  'purchases',
  'purchase_items',
  'supplier_payments',
  'consignments',
  'consignment_items',
  'consignment_payments',
  'returns',
  'return_items',
  'exchanges',
  'exchange_items',
  'warranties',
  'campaigns',
  'campaign_metrics',
  'meta_pixel_events',
  'audit_logs',
  'documents',
  'discounts',
  'faqs',
  'testimonials',
  'reservation_sweep_runs',
];

export interface BackupResult {
  data: Record<string, unknown[]>;
  errors: string[];
  totalRows: number;
}

// Lee todas las tablas de negocio para armar la copia de seguridad.
export async function buildBackup(): Promise<BackupResult> {
  const out: Record<string, unknown[]> = {};
  const errors: string[] = [];
  let totalRows = 0;

  if (!supabase) {
    return { data: out, errors: ['Sin conexión a la base de datos.'], totalRows: 0 };
  }

  for (const table of BACKUP_TABLES) {
    const { data, error } = await supabase.from(table).select('*');
    if (error) {
      errors.push(`${table}: ${error.message}`);
      continue;
    }
    const rows = (data ?? []) as unknown[];
    out[table] = rows;
    totalRows += rows.length;
  }

  return { data: out, errors, totalRows };
}

// Genera el archivo de respaldo (Blob + object URL) SIN disparar la descarga.
// Devuelve la URL lista para usarse tanto en la descarga automática como en el
// enlace manual de respaldo. La estructura del JSON incluye metadatos de control
// (version, timestamp, generated_by) y los datos agrupados por tabla.
export interface BackupFile {
  filename: string;
  url: string;
  sizeBytes: number;
  totalRows: number;
}

export function createBackupFile(
  payload: Record<string, unknown[]>,
  generatedBy?: string,
): BackupFile {
  const now = new Date();
  const dateStamp = now.toISOString().slice(0, 10);
  const filename = `PCP_Backup_${dateStamp}.json`;
  const totalRows = Object.values(payload).reduce((sum, rows) => sum + rows.length, 0);
  const body = JSON.stringify(
    {
      version: '1.0',
      timestamp: now.toISOString(),
      generated_by: generatedBy ?? 'admin',
      system: 'PCP',
      data: payload,
    },
    null,
    2,
  );
  const url = createTextBlobUrl(body, 'application/octet-stream');
  return { filename, url, sizeBytes: new Blob([body]).size, totalRows };
}